import { openDB, type DBSchema } from 'idb'

interface TwoDomyDB extends DBSchema {
  calendar_entries: {
    key: string
    value: {
      id: string
      child_id: string
      family_id: string
      date: string
      location: 'home_a' | 'home_b'
      comment: string | null
      created_by: string
      created_at: string
      updated_at: string
      sync_status: 'synced' | 'pending' | 'error'
    }
    indexes: {
      'by-child': string
      'by-date': string
      'by-sync-status': string
    }
  }
  children: {
    key: string
    value: {
      id: string
      family_id: string
      name: string
      birth_date: string | null
      created_at: string
    }
    indexes: {
      'by-family': string
    }
  }
  pending_changes: {
    key: string
    value: {
      id: string
      table: string
      action: 'create' | 'update' | 'delete'
      data: any
      timestamp: number
    }
    indexes: {
      'by-table': string
      'by-timestamp': number
    }
  }
  encryption_keys: {
    key: string
    value: {
      id: string
      userId?: string
      familyId?: string
      encryptedKey?: string
      salt?: string
      verifier?: string
      version?: number
      createdAt: string
      // Pola dla przechowywania haseł
      sessionOnly?: boolean
      expiresAt?: string | null
    }
    indexes: {
      'by-user': string
      'by-family': string
    }
  }
}

export const initDB = async () => {
  return await openDB<TwoDomyDB>('2domy-db', 2, {
    upgrade(db, oldVersion) {
      // v1 → v2: przebudowa store 'calendar_entries' (usunięcie pola segment)
      if (oldVersion < 1) {
        // Fresh install path – create all stores
        const calendarStore = db.createObjectStore('calendar_entries', { keyPath: 'id' })
        calendarStore.createIndex('by-child', 'child_id')
        calendarStore.createIndex('by-date', 'date')
        calendarStore.createIndex('by-sync-status', 'sync_status')

        const childrenStore = db.createObjectStore('children', { keyPath: 'id' })
        childrenStore.createIndex('by-family', 'family_id')

        const pendingStore = db.createObjectStore('pending_changes', { keyPath: 'id' })
        pendingStore.createIndex('by-table', 'table')
        pendingStore.createIndex('by-timestamp', 'timestamp')

        const encryptionKeysStore = db.createObjectStore('encryption_keys', { keyPath: 'id' })
        encryptionKeysStore.createIndex('by-user', 'userId')
        encryptionKeysStore.createIndex('by-family', 'familyId')
      }

      if (oldVersion < 2) {
        // Safely drop and recreate calendar_entries store (clear stale records with segment)
        if (db.objectStoreNames.contains('calendar_entries')) {
          db.deleteObjectStore('calendar_entries')
        }
        const calendarStoreV2 = db.createObjectStore('calendar_entries', { keyPath: 'id' })
        calendarStoreV2.createIndex('by-child', 'child_id')
        calendarStoreV2.createIndex('by-date', 'date')
        calendarStoreV2.createIndex('by-sync-status', 'sync_status')
      }
    },
  })
}

// Singleton instance
let dbPromise: ReturnType<typeof initDB> | null = null

export const getDB = () => {
  if (!dbPromise) {
    dbPromise = initDB()
  }
  return dbPromise
} 